import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { App } from './App.js';
import { firstRunDestination, markOnboardingSeen, mountWhenSessionRestored } from './auth/session.js';
import { stateSeamOpen } from './states/boundary.js';
import './styles.css';

const root = document.getElementById('root');
if (!root) throw new Error('#root missing from index.html');

/**
 * A first visit to `/` starts at A1 · ভাষা নির্বাচন, once — see
 * `auth/session.ts § WHERE A FIRST VISIT BEGINS` for who counts as first, why only from
 * `/`, and why never in the build the visual gate measures.
 *
 * HOW, AND WHY NOT `location.replace`. The router is created when `App.tsx` is imported,
 * which is before this line runs, and it has already read `/`. A `location.replace` would
 * fix that with a second page load on the visit that decides whether a person comes back,
 * on the connection least able to afford it. Instead the address is replaced in place and
 * the router is told the way a browser tells it about back and forward — a `popstate` — so
 * it moves before anything is painted. `history.state` is carried over untouched, so the
 * router's own bookkeeping in it (its entry index) stays what it was. And if the router is
 * ever created lazily instead, it reads the replaced address itself and the event is
 * simply unheard: the result is the same either way.
 *
 * The device is marked BEFORE the move, so a person who leaves A1 and opens `/` again
 * gets home: a first run is one visit, not a gate.
 */
const firstRun = firstRunDestination({
  hostname: window.location.hostname,
  pathname: window.location.pathname,
  search: window.location.search,
  harness: stateSeamOpen(),
});
if (firstRun !== null) {
  markOnboardingSeen();
  window.history.replaceState(window.history.state, '', firstRun);
  window.dispatchEvent(new PopStateEvent('popstate', { state: window.history.state as unknown }));
}

/**
 * The session is brought back BEFORE the first render, and only when there is one.
 *
 * The refresh token survives a reload in `localStorage`; the access token lives in a
 * module variable in `api/client.ts` and does not. Until this line existed nothing spent
 * the one to recover the other, so every reload signed the customer out — see
 * `auth/session.ts § THE RELOAD THAT SIGNED EVERYBODY OUT` for the whole argument,
 * including why a visitor with no stored token still mounts synchronously and why the
 * outcome never decides whether the app appears.
 */
mountWhenSessionRestored(() => {
  createRoot(root).render(
    <StrictMode>
      <App />
    </StrictMode>,
  );
});

/**
 * Register the service worker — see `src/sw.ts` for the caching rules and the reasoning.
 *
 * AFTER `load`, NOT DURING IT. Registration kicks off the install, and install downloads
 * the whole precache: the shell, the framework chunk, the app chunk and the 155 kB
 * Bangla face. Starting that while the first screen is still fetching its own bytes puts
 * the two in competition for a connection that, on 2G, has room for one. The customer
 * would pay for offline support with a slower first paint, which is the wrong trade on
 * the visit that decides whether they come back. Deferred to the load event, the
 * precache happens on an idle connection and costs the first screen nothing.
 *
 * `updateViaCache: 'none'` is the line that stops a stale shell. Without it the browser
 * may serve sw.js itself from the HTTP cache, and a worker that cannot be re-fetched is
 * a worker that can never be replaced — the exact failure mode that makes a bad service
 * worker worse than none.
 *
 * DEV IS EXCLUDED. `vite dev` serves unbundled modules and no sw.js; a worker there
 * would cache the dev server's own module graph and hand back yesterday's component.
 */
if (import.meta.env.PROD && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    void navigator.serviceWorker
      .register('/sw.js', { scope: '/', updateViaCache: 'none' })
      .catch(() => {
        /*
         * Swallowed, deliberately and narrowly.
         *
         * Registration rejects for reasons that are all about the ENVIRONMENT, never
         * about this build: an insecure origin, a browser with workers disabled, a
         * private window, a corporate proxy rewriting the script's content type. Every
         * one of them leaves an app that works — it just works without the offline
         * shell. There is no drawn state for "offline support is unavailable" because
         * there is nothing the customer could do about it, and `packages/contracts`
         * deliberately has no generic failure to fall back on.
         *
         * Nothing is logged: the rejection carries the request URL, and this app is
         * loaded on shared phones where the console is not private.
         */
      });
  });
}
